import { useUserInfo } from "@next-platform/client-common-core";
import {
  type CapabilityKey,
  type SubCapabilityKey,
  useApplicationCapabilitiesAllowed,
  useApplicationCapabilityReady,
} from "@next-platform/client-common-core/infra/application/capabilities";
import { AnySubCapabilityKey } from "@next-platform/packages-ts-types";
import { type ReactNode } from "react";
import { Navigate } from "react-router-dom";

import { RELATIVE_ROUTES } from "@/constants";

type CapabilityRouteGuardProps = {
  capability: CapabilityKey | SubCapabilityKey;
  redirectTo?: string;
  /**
   * When true, anonymous (not-logged-in) visitors are allowed through even if
   * the capability is not allowed for them. The rendered page is then
   * responsible for showing an appropriate anonymous experience. Signed-in
   * users are still gated by the capability.
   */
  allowAnonymous?: boolean;
  children: ReactNode;
};

function getAbsoluteRedirectPath(path: string) {
  return path.startsWith("/") ? path : `/${path}`;
}

export default function CapabilityRouteGuard({
  capability,
  redirectTo = RELATIVE_ROUTES.accessDenied,
  allowAnonymous = false,
  children,
}: CapabilityRouteGuardProps) {
  const isReady = useApplicationCapabilityReady();
  const isAllowed = useApplicationCapabilitiesAllowed(capability as AnySubCapabilityKey);
  const { isAuthenticated, isAuthLoaded } = useUserInfo();
  const absoluteRedirectTo = getAbsoluteRedirectPath(redirectTo);

  // Anonymous visitors on an allowAnonymous route bypass the capability gate.
  // Wait for auth to load first so we don't redirect a signed-in user before
  // their capabilities resolve.
  if (allowAnonymous && isAuthLoaded && !isAuthenticated) {
    return <>{children}</>;
  }

  if (!isReady || !isAuthLoaded) {
    return null;
  }

  if (!isAllowed) {
    return <Navigate to={absoluteRedirectTo} replace />;
  }

  return <>{children}</>;
}
